<template>
  <div>
    <CapabilitiesList :capabilities="capabilities" />
    <LoadingIcon
      v-if="isLoading"
      :class="$style.container"
      :scale="2"
    />
    <div
      v-else
      :class="$style.container"
    >
      <Button
        v-if="isApnsPushTopicEmpty"
        :href="mdmLink"
        :text="configureBtnText"
        @click="handleConfigureProfile"
      />
      <div v-else>
        <IconButton
          ref="downloadButton"
          icon="fileDownload"
          :text="downloadBtnText"
          @click="downloadProfile"
        />
        <CopyWell
          :content="downloadUrl"
          hideTextarea
          :label="copyBtnText"
          @copyToClipboard="handleCopyLink"
        />
      </div>
    </div>
    <CompatibilityNotes
      v-if="compatibilityNotes"
      :compatibilityNotes="compatibilityNotes"
    />
  </div>
</template>

<script>
import {
  Button,
  IconButton,
  LoadingIcon,
} from '@jumpcloud/ui-components';
import {
  ItemTypeConsts,
  ItemTypeRoutes,
  SystemCapabilitiesConsts,
} from 'client/util/Constants';
import { eventLocations, eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import CapabilitiesList from 'client/systems/CapabilitiesList.vue';
import CompatibilityNotes from 'client/systems/CompatibilityNotes.vue';
import CopyWell from 'client/components/FormValidation/CopyWell.vue';
import DownloadUtil from 'client/util/DownloadUtil';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import MdmSettingsMixin from 'client/MDM/mixins/MdmSettingsMixin';

const {
  description,
  displayLogic,
  location,
  section,
  text,
} = eventProperties;

export default {
  name: 'MdmEnrollmentInstructions',

  components: {
    Button,
    CapabilitiesList,
    CompatibilityNotes,
    CopyWell,
    IconButton,
    LoadingIcon,
  },

  DownloadUtil,
  EventHelper,

  mixins: [
    MdmSettingsMixin,
  ],

  props: {
    compatibilityNotes: {
      type: Object,
      default() {
        return null;
      },
      validator(value) {
        if (!value) {
          return true;
        }

        return !!(value.header && value.text);
      },
    },
  },

  data: () => ({
    capabilities: [
      {
        label: SystemCapabilitiesConsts.macAuthentication,
        included: true,
      },
      {
        label: SystemCapabilitiesConsts.commandDeployment,
        included: true,
      },
      {
        label: SystemCapabilitiesConsts.deviceTrust,
        included: true,
      },
      {
        label: SystemCapabilitiesConsts.policyMgmt,
        included: true,
      },
      {
        label: SystemCapabilitiesConsts.softwareMgmt,
        included: true,
      },
      {
        label: SystemCapabilitiesConsts.remote,
        included: true,
      },
      {
        label: SystemCapabilitiesConsts.zeroTouch,
        included: true,
      },
      {
        label: SystemCapabilitiesConsts.osPatchMgmt,
        included: true,
      },
    ],
    configureBtnText: 'Configure MDM',
    copyBtnText: 'Copy Download Link',
    downloadBtnText: 'Download MDM Enrollment Profile',
    isLoading: false,
    mdmLink: ItemTypeRoutes[ItemTypeConsts.mdmSettings],
  }),

  mounted() {
    this.handleMounted();
  },

  methods: {
    downloadProfile() {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'Downloads the MDM Enrollment Profile',
        [displayLogic]: 'Visible if user has configured MDM',
        [location]: eventLocations.devicesPage,
        [section]: 'Add a Mac Device Sidebar',
        [text]: this.downloadBtnText,
      });

      this.handleDownloadProfile();
    },

    handleConfigureProfile() {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'Navigates to MDM page',
        [displayLogic]: 'Visible if user has not configured MDM',
        [location]: eventLocations.devicesPage,
        [section]: 'Add a Mac Device Sidebar',
        [text]: this.configureBtnText,
      });
    },

    handleCopyLink() {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'Copies the MDM Enrollment Profile download link',
        [displayLogic]: 'Visible if user has configured MDM',
        [location]: eventLocations.devicesPage,
        [section]: 'Add a Mac Device Sidebar',
        [text]: this.copyBtnText,
      });
    },

    async handleMounted() {
      this.isLoading = true;

      try {
        await this.fetchMdmSettings();
        if (this.getDmId) {
          this.setEnrollmentProfiles();
          this.retrieveEnrollmentProfileDownloadUrl();
        }
        this.isLoading = false;
      } catch {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style module scoped>
.container {
  margin: var(--jc-spacer-large) 0;
}
</style>
